import {
  formatUserDisplay,
  USER_FORMAT_PRESETS,
  UserFormatConfig,
  UserData
} from '../text/userFormatting';

interface UserOption {
  name: string;  // The actual value (userId or empty string for "All")
  label: string; // The display text
  data?: UserData; // Optional: Keep original user data for reference
}

interface UserOptionsConfig {
  /** Format configuration for user labels */
  formatConfig?: UserFormatConfig;

  /** Label for the "All users" option */
  allUsersLabel?: string;

  /** Whether to include "All users" option */
  includeAllUsers?: boolean;

  /** Position of "All users" option */
  allUsersPosition?: 'first' | 'last' | 'none';

  /** Custom filter function to include/exclude users */
  filterUsers?: (user: UserData) => boolean;

  /** Custom sort function for options */
  sortFunction?: (a: UserOption, b: UserOption) => number;

  /** Whether to sort options (default: true) */
  enableSort?: boolean;

  /** Sort configuration */
  sortConfig?: {
    caseSensitive?: boolean;
    locale?: string;
    numeric?: boolean;
  };

  /** Transform function applied after formatting */
  postFormatTransform?: (label: string, user: UserData) => string;

  /** Include original user data in options */
  includeUserData?: boolean;

  /** Group options by a criteria */
  groupBy?: (user: UserData) => string | null;

  /** Add custom options */
  customOptions?: UserOption[];

  /** Remove duplicate users based on a key */
  dedupeBy?: keyof UserData | ((user: UserData) => string);
}

// Preset configurations
const USER_OPTIONS_PRESETS = {
  /** Default configuration - Full name with shortId */
  default: {
    formatConfig: USER_FORMAT_PRESETS.default,
    includeAllUsers: true,
    allUsersPosition: 'first',
    enableSort: true,
    includeUserData: false
  } as UserOptionsConfig,

  /** Email-based display */
  emailBased: {
    formatConfig: USER_FORMAT_PRESETS.withEmail,
    includeAllUsers: true,
    allUsersPosition: 'first',
    enableSort: true
  } as UserOptionsConfig,

  /** Compact display with initials */
  compact: {
    formatConfig: USER_FORMAT_PRESETS.compact,
    includeAllUsers: true,
    allUsersPosition: 'first',
    enableSort: true
  } as UserOptionsConfig,

  /** User ID focused */
  withUserId: {
    formatConfig: USER_FORMAT_PRESETS.withUserId,
    includeAllUsers: true,
    allUsersPosition: 'first',
    enableSort: true
  } as UserOptionsConfig,

  /** No "All users" option */
  noAllUsers: {
    formatConfig: USER_FORMAT_PRESETS.default,
    includeAllUsers: false,
    enableSort: true
  } as UserOptionsConfig,

  /** Reversed name format */
  reversed: {
    formatConfig: USER_FORMAT_PRESETS.reversed,
    includeAllUsers: true,
    allUsersPosition: 'first',
    enableSort: true
  } as UserOptionsConfig
};

/**
 * Build signee options from users array with configurable formatting
 */
export function buildUserOptions(
  users: UserData[],
  config: UserOptionsConfig = USER_OPTIONS_PRESETS.default
): UserOption[] {
  const {
    formatConfig = USER_FORMAT_PRESETS.default,
    allUsersLabel = "All users",
    includeAllUsers = true,
    allUsersPosition = 'first',
    filterUsers,
    sortFunction,
    enableSort = true,
    sortConfig = { caseSensitive: false, locale: undefined, numeric: false },
    postFormatTransform,
    includeUserData = false,
    customOptions = [],
    dedupeBy
  } = config;

  let options: UserOption[] = [];

  // Add "All users" option if needed
  const allUsersOption: UserOption = {
    name: "",
    label: allUsersLabel
  };

  // Filter users if filter function is provided
  let filteredUsers = filterUsers ? users.filter(filterUsers) : users;

  // Deduplicate users if needed
  if (dedupeBy) {
    const seen = new Set<string>();
    filteredUsers = filteredUsers.filter(user => {
      const key = typeof dedupeBy === 'function'
        ? dedupeBy(user)
        : String(user[dedupeBy] || '');

      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  // Build options from users
  filteredUsers.forEach(user => {
    if (!user.userId) return; // Skip users without userId

    let label = formatUserDisplay(user, formatConfig);

    // Apply post-format transformation if provided
    if (postFormatTransform) {
      label = postFormatTransform(label, user);
    }

    const option: UserOption = {
      name: user.userId,
      label: label
    };

    // Include user data if requested
    if (includeUserData) {
      option.data = user;
    }

    options.push(option);
  });

  // Add custom options
  options.push(...customOptions);

  // Sort options if enabled
  if (enableSort && !sortFunction) {
    options.sort((a, b) => {
      return a.label.localeCompare(b.label, sortConfig.locale, {
        sensitivity: sortConfig.caseSensitive ? 'case' : 'base',
        numeric: sortConfig.numeric
      });
    });
  } else if (sortFunction) {
    options.sort(sortFunction);
  }

  // Add "All users" option at the specified position
  if (includeAllUsers && allUsersPosition !== 'none') {
    if (allUsersPosition === 'first') {
      options.unshift(allUsersOption);
    } else if (allUsersPosition === 'last') {
      options.push(allUsersOption);
    }
  }

  return options;
}

/**
 * Build grouped signee options
 */
export function buildGroupedUserOptions(
  users: UserData[],
  config: UserOptionsConfig = USER_OPTIONS_PRESETS.default
): Map<string, UserOption[]> {
  const { groupBy } = config;

  if (!groupBy) {
    return new Map([['default', buildUserOptions(users, config)]]);
  }

  const groups = new Map<string, UserData[]>();

  // Group users
  users.forEach(user => {
    const groupKey = groupBy(user) || 'Other';
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(user);
  });

  // Build options for each group
  const groupedOptions = new Map<string, UserOption[]>();
  groups.forEach((groupUsers, groupKey) => {
    const options = buildUserOptions(groupUsers, {
      ...config,
      includeAllUsers: false // Don't include "All users" in groups
    });
    groupedOptions.set(groupKey, options);
  });

  return groupedOptions;
}

/**
 * Create a custom signee options builder with preset configuration
 */
export function createUserOptionsBuilder(config: UserOptionsConfig) {
  return (users: UserData[]) => buildUserOptions(users, config);
}

/**
 * Search/filter signee options
 */
export function filterUserOptions(
  options: UserOption[],
  searchTerm: string,
  includeAllUsersInSearch = false
): UserOption[] {
  if (!searchTerm || searchTerm.trim() === '') {
    return options;
  }

  const term = searchTerm.toLowerCase();

  return options.filter(option => {
    // Always include "All users" if specified
    if (!includeAllUsersInSearch && option.name === '') {
      return true;
    }

    // Search in label
    if (option.label.toLowerCase().includes(term)) {
      return true;
    }

    // Search in user data if available
    if (option.data) {
      const searchableFields = [
        option.data.email,
        option.data.name,
        option.data.givenName,
        option.data.familyName,
        option.data.userId
      ].filter(Boolean).join(' ').toLowerCase();

      return searchableFields.includes(term);
    }

    return false;
  });
}

/**
 * Get selected user from options
 */
export function getSelectedUser(
  options: UserOption[],
  selectedValue: string
): UserOption | null {
  return options.find(opt => opt.name === selectedValue) || null;
}

/**
 * Check if value represents "All users"
 */
export function isAllUsersSelected(value: string | UserData | UserOption): boolean {
  if (typeof value === 'string') {
    return value === '';
  }
  if ('name' in value) {
    return value.name === '';
  }
  return false;
}

// Re-export types for external use
export type { UserOption, UserOptionsConfig };

